import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del } from '@utils/axios_api';
import { toast } from 'react-toastify';

const QUERY_KEY = ['allMenus'];

/**
 * 메뉴 CRUD 훅 (슈퍼관리자 전용)
 * - 전체 메뉴 트리 조회
 * - 메뉴 생성 / 수정 / 삭제
 */
const useMenuManagement = () => {
    const queryClient = useQueryClient();

    // ── 전체 메뉴 트리 ──────────────────────────────────────────────────
    const { data: menuTree = [], isLoading } = useQuery({
        queryKey: QUERY_KEY,
        queryFn: () => get('/super/comm/menu/list').then(res => res?.result ?? []),
        staleTime: 5 * 60 * 1000,
    });

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: ['myMenus'] }); // 헤더 동적 메뉴 갱신
    };

    // ── 메뉴 생성 ────────────────────────────────────────────────────────
    const createMenuMutation = useMutation({
        mutationFn: (dto) => post('/super/comm/menu', dto),
        onSuccess: () => {
            toast.success('메뉴가 등록되었습니다.');
            invalidate();
        },
        onError: (err) => {
            const msg = err?.response?.data?.message ?? '메뉴 등록에 실패했습니다.';
            toast.error(msg);
        },
    });

    // ── 메뉴 수정 ────────────────────────────────────────────────────────
    const updateMenuMutation = useMutation({
        mutationFn: ({ menuCode, ...dto }) =>
            put(`/super/comm/menu/${menuCode}`, dto),
        onSuccess: () => {
            toast.success('메뉴가 수정되었습니다.');
            invalidate();
        },
        onError: () => toast.error('메뉴 수정에 실패했습니다.'),
    });

    // ── 메뉴 삭제 ────────────────────────────────────────────────────────
    const deleteMenuMutation = useMutation({
        mutationFn: (menuCode) => del(`/super/comm/menu/${menuCode}`),
        onSuccess: () => {
            toast.success('메뉴가 삭제되었습니다.');
            invalidate();
        },
        onError: (err) => {
            const msg = err?.response?.data?.message ?? '메뉴 삭제에 실패했습니다.';
            toast.error(msg);
        },
    });

    // ── 메뉴 순서 일괄 저장 ─────────────────────────────────────────────
    const reorderMenusMutation = useMutation({
        mutationFn: (sortList) => put('/super/comm/menu/reorder', sortList),
        onSuccess: () => {
            toast.success('순서가 저장되었습니다.');
            invalidate();
        },
        onError: () => toast.error('순서 저장에 실패했습니다.'),
    });

    return {
        menuTree,
        isLoading,
        createMenu: (dto) => createMenuMutation.mutate(dto),
        updateMenu: (menuCode, dto) => updateMenuMutation.mutate({ menuCode, ...dto }),
        deleteMenu: (menuCode) => deleteMenuMutation.mutate(menuCode),
        reorderMenus: reorderMenusMutation.mutate,
        isCreating: createMenuMutation.isPending,
        isUpdating: updateMenuMutation.isPending,
        isDeleting: deleteMenuMutation.isPending,
        isReordering: reorderMenusMutation.isPending,
    };
};

export default useMenuManagement;
